import { MapPinned, Moon } from 'lucide-react'
import { formatCurrency } from '@/lib/format'
import type { AreaSurcharge } from '@/types/api'
import { Badge } from '@/components/ui/StatusBadge'
import { describeEnd, describeEndCharge, describeLateEnds } from './describe'
import styles from './AreaSurchargeBadge.module.css'

/**
 * A Cairo booking's area surcharge as one chip — "Area +E£180" — for the
 * tables and the drawer's header.
 *
 * Renders nothing when the two ends added nothing, which is every booking
 * outside Cairo, every one before sub-areas existed, and a trip within the
 * primary group: a chip saying "+0" on half the rows would be noise. The
 * hover text names both ends.
 *
 * Inside the fare, not on top of it: the customer was shown each package with
 * this already in its price.
 */
export function AreaSurchargeBadge({ area }: { area: AreaSurcharge }) {
  if (!area.any || area.totalMinor <= 0) return null

  return (
    <Badge
      tone="warning"
      title={`Cairo area surcharge, inside the fare — ${describeEnd(area.pickup)} → ${describeEnd(area.dropoff)}`}
    >
      <MapPinned aria-hidden="true" className={styles.icon} />
      Area <span className={styles.price}>+{formatCurrency(area.total)}</span>
    </Badge>
  )
}

/**
 * A Cairo booking's late-night charge as one chip — "Late +E£45" — beside the
 * area chip.
 *
 * A booking from an app that could not show the late-night charge gets a chip
 * too, "Late · not charged": it was late, and the fare does not say so.
 * Nothing otherwise, including a late end that added nothing.
 */
export function LateChargeBadge({ area }: { area: AreaSurcharge }) {
  const ends = describeLateEnds(area)

  if (area.lateStatus === 'NOT_SHOWN') {
    return (
      <Badge
        tone="neutral"
        title={`Late at night (${ends}), but booked on an older app that does not price the pickup time — booked at the daytime price`}
      >
        <Moon aria-hidden="true" className={styles.icon} />
        Late · not charged
      </Badge>
    )
  }
  if (area.lateChargeMinor <= 0) return null

  return (
    <Badge tone="info" title={`Late-night charge, inside the fare — ${ends} late`}>
      <Moon aria-hidden="true" className={styles.icon} />
      Late <span className={styles.price}>+{formatCurrency(area.lateCharge)}</span>
    </Badge>
  )
}

/**
 * Both ends and what each added, for the places a fare is read back line by
 * line: the trip drawer's charges, the approval dialog, a change review.
 *
 * Shown even when the total is zero, because there the question is "why is
 * there no surcharge?", and "Zamalek · Downtown" is the answer. A late end
 * says what being late added; the late-night charge gets its own line.
 */
export function AreaSurchargeDetails({ area }: { area: AreaSurcharge }) {
  if (!area.any) return null

  const late = area.lateChargeMinor > 0 || area.lateStatus === 'NOT_SHOWN'

  return (
    <dl className={styles.details}>
      <div className={styles.row}>
        <dt>Pickup</dt>
        <dd>
          <span>{describeEnd(area.pickup)}</span>
          <span className={styles.amount}>{describeEndCharge(area.pickup, area.lateStatus)}</span>
        </dd>
      </div>
      <div className={styles.row}>
        <dt>Drop-off</dt>
        <dd>
          <span>{describeEnd(area.dropoff)}</span>
          <span className={styles.amount}>{describeEndCharge(area.dropoff, area.lateStatus)}</span>
        </dd>
      </div>
      <div className={`${styles.row} ${styles.totalRow}`}>
        <dt>Total</dt>
        <dd>
          <span className={styles.amount}>
            {area.totalMinor > 0 ? formatCurrency(area.total) : 'No surcharge'}
          </span>
        </dd>
      </div>
      {late ? (
        <div className={`${styles.row} ${styles.lateRow}`}>
          <dt>Late-night</dt>
          <dd>
            {area.lateStatus === 'NOT_SHOWN' ? (
              <span className={styles.note}>Not charged — booked on an older app, at the daytime price</span>
            ) : (
              <span className={styles.amount}>+{formatCurrency(area.lateCharge)}</span>
            )}
          </dd>
        </div>
      ) : null}
    </dl>
  )
}
